import { CustomActionDiscriminator } from 'autogen/swagger/all';
import type { ConnectedContentInlineModel } from 'autogen/swagger/all';
import HorizontalLayout from 'components/connectedContent/shared/horizontalLayout';
import VerticalLayout from 'components/connectedContent/shared/verticalLayout';
import { useQuickView } from 'components/quickView';
import QuickViewArticle from 'components/QuickViewArticle/QuickViewArticle';
import {
    guardIsPageActionOfTypeCustomAction,
    guardIsPageActionOfTypeLinkAction
} from 'components/shared/Routing/components/shared/helpers';
import ConnectedContentSurveyItem from 'components/surveySystem/components/ConnectedContentSurveyItem';
import { useOpenSurveyQuickView } from 'components/surveySystem/shared/hooks/useOpenSurveyQuickView';
import { postAdformClickTracking } from 'modules/tracking/adformClickTracking';
import { useEffect } from 'react';
import type { IExtendedInlineModel } from './shared/interfaces';

export interface IInlineConnectedContent {
    model: IExtendedInlineModel;
}

export default function InlineConnectedContent({ model }: { model: ConnectedContentInlineModel }): JSX.Element {
    const {
        widthType,
        placement,
        link,
        isQuickView,
        designTemplate: { layoutType },
        openAsModalProactively,
        adformTracking
    } = model;
    const linkAction = guardIsPageActionOfTypeLinkAction(link?.pageAction);
    const customAction = guardIsPageActionOfTypeCustomAction(link?.pageAction);
    const identifier = `placement-${placement}_size-${widthType}`;
    const isColumnLayout = widthType !== 'One' && layoutType === 'Horizontal';
    const { toggle } = useQuickView(identifier);
    const { openSurveyQuickViewBySurveyId } = useOpenSurveyQuickView();

    useEffect(() => {
        if (customAction && openAsModalProactively) {
            const surveyAction = CustomActionDiscriminator(
                customAction.action,
                () => null,
                () => null,
                () => null,
                (surveyModel) => surveyModel,
                () => null,
                () => null
            );

            if (surveyAction?.surveyId) openSurveyQuickViewBySurveyId({ surveyId: surveyAction.surveyId });
        }
    }, [link, openAsModalProactively]);

    const onClick = () => {
        if (!linkAction) {
            if (isQuickView) {
                toggle(true);
            }
        }

        if (adformTracking?.clickTracking) {
            postAdformClickTracking(adformTracking.clickTracking);
        }
    };

    /* 
        We display the first survey question directly in the connectedContent item if it has surveyQuestion
    */
    if (model.surveyQuestion) {
        return <ConnectedContentSurveyItem model={model} />;
    }

    return (
        <>
            {linkAction && isQuickView && <QuickViewArticle quickViewId={identifier} linkAction={linkAction.action} />}

            {isColumnLayout ? (
                <HorizontalLayout model={model} onClick={onClick} />
            ) : (
                <VerticalLayout model={model} onClick={onClick} />
            )}
        </>
    );
}
